'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import InnerPageHeader from '@/components/InnerPageHeader';
import Toast from '@/components/Toast';

export default function AdminLogin() {
  const router = useRouter();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch('/api/admin/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('authToken', data.token);
        localStorage.setItem('user', JSON.stringify({
          userId: data.user.id,
          email: data.user.email,
          role: data.user.role,
          fullName: data.user.fullName
        }));
        
        setToast({ message: 'Login successful!', type: 'success' });
        setTimeout(() => {
          router.push('/admin/dashboard');
        }, 1500);
      } else {
        setToast({ message: data.error || 'Login failed', type: 'error' });
      }
    } catch (error) {
      setToast({ message: 'Login failed. Please try again.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F6EFED]">
      <InnerPageHeader />

      <section className="py-12">
        <div className="max-w-[500px] mx-auto px-[20px]">
          <div className="bg-white rounded-[24px] p-8 md:p-12 shadow-sm">
            {/* Header */}
            <div className="text-center mb-8">
              <h1 className="font-['Gotham_Rounded',sans-serif] text-[48px] font-bold text-[#2E2A3A] mb-4">
                Admin Login
              </h1>
              <p className="font-['Archivo',sans-serif] text-[18px] text-[#6e6672]">
                Access the system administration panel
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email */}
              <div>
                <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="admin@example.com"
                  required
                  className="w-full px-4 py-3 border border-[#d7d7d7] rounded-[12px] bg-white font-['Archivo',sans-serif] text-[16px] focus:outline-none focus:border-[#8a7296] transition-colors"
                />
              </div>

              {/* Password */}
              <div>
                <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-2">
                  Password
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Enter your password"
                  required
                  className="w-full px-4 py-3 border border-[#d7d7d7] rounded-[12px] bg-white font-['Archivo',sans-serif] text-[16px] focus:outline-none focus:border-[#8a7296] transition-colors"
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 bg-[#8A7296] text-white font-['Archivo',sans-serif] text-[16px] font-medium rounded-[12px] hover:bg-[#7a647d] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>

            {/* Footer */}
            <div className="text-center mt-8">
              <p className="font-['Archivo',sans-serif] text-[14px] text-[#6e6672]">
                Need access? Contact system administrator
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}
